import React, { useState, useEffect } from "react";
import RefreshButton from "../components/admin/RefreshButton";
import { toast } from "react-hot-toast";
import settingsService from "../services/SettingsService";
import axios from "axios";
import { FiDatabase, FiWifi, FiWifiOff } from "react-icons/fi";
import { PRIMARY_API_URL } from "../config/api";
import ConfirmationModal from "../components/ConfirmationModal";

const Settings = () => {
  console.log("Settings component rendering");
  
  const [settings, setSettings] = useState({
    pricing: {
      baseFee: 3,
      pricePerKm: 2,
      pricePerLuggage: 1,
      surchargePerPassenger: 0.2
    },
    defaultLocation: {
      name: "Lasmari Apraos",
      lat: 39.796276,
      lng: 19.884997
    },
    company: {
      name: "Lasmari Chauffeurs",
      email: "info@lasmari.com",
      phone: "+30 2663098633",
      website: "lasmari.com"
    },
    business: {
      name: "Lasmari Chauffeurs",
      address: "North Corfu",
      phone: "+30 2663098633",
      email: "info@lasmari.com",
      website: "https://lasmari.com"
    }
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ text: "", type: "" });
  const [activeSetting, setActiveSetting] = useState("pricing");
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUser, setEditUser] = useState({
    name: "",
    email: "",
    password: "",
    role: "admin",
    phone: "",
    status: "active"
  });
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    role: "admin",
    phone: ""
  });
  
  const [error, setError] = useState(null);
  const [dbConnected, setDbConnected] = useState(true); // Track if database is connected
  
  // Check database connection on load
  useEffect(() => {
    // Only check once when component mounts
    checkDatabaseConnection();
    
    // Set up a polling interval for checking connection status (every 30 seconds)
    const connectionInterval = setInterval(() => {
      checkDatabaseConnection();
    }, 30000); // 30 seconds
    
    // Clean up interval on component unmount
    return () => {
      clearInterval(connectionInterval);
    };
  }, []); // Empty dependency array - only run on mount/unmount
  
  // Function to check database connectivity
  const checkDatabaseConnection = async () => {
    try {
      console.log('Checking database connection...');
      const response = await axios.get(`${PRIMARY_API_URL}/api/settings`);
      setDbConnected(true);
      console.log("Database connection successful");
    } catch (err) {
      setDbConnected(false);
      console.warn("Database connection failed:", err.message);
    }
  };
  
  // Simulated loading of settings
  useEffect(() => {
    console.log("Initial useEffect running");
    // Fetch settings from backend - we only need to do this once on component mount
    fetchSettings();
  }, []); // Empty dependency array to only run once
  
  // Function to fetch settings from the API
  const fetchSettings = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await settingsService.getSettings();
      
      if (data) {
        console.log("Settings loaded:", data);
        setSettings(data);
      }
    } catch (err) {
      console.error("Failed to load settings:", err);
      setError("Failed to load settings. Using defaults.");
      toast.error("Failed to load settings from server. Using default values.");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleChange = (section, field, value) => {
    // Special handling for percentage values
    if (section === "pricing" && field === "surchargePerPassenger") {
      // Convert percentage to decimal (e.g., 20 to 0.2)
      value = value / 100;
    }
    
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };
  
  const handleSave = async () => {
    setIsLoading(true);
    setSaveMessage({ text: "", type: "" });
    
    try {
      // Try to reset API connection to database on each save attempt
      settingsService.resetApiStatus();
      
      // Save settings to database using the service
      const result = await settingsService.updateSettings(settings);
      
      if (result.success) {
        console.log("Settings saved:", result);
        
        // Show appropriate message based on where settings were saved
        if (result.localOnly) {
          setSaveMessage({ 
            text: "Database connection failed. Settings saved locally as a backup.", 
            type: "warning" 
          });
          toast.success("Settings saved locally only");
        } else {
          setSaveMessage({ 
            text: "Settings saved successfully to database", 
            type: "success" 
          });
          toast.success("Settings saved to database");
        }
        
        // Update local state with any changes returned from the server
        if (result.settings) {
          setSettings(result.settings);
        }
      } else {
        throw new Error("Failed to save settings");
      }
    } catch (err) {
      console.error("Error saving settings:", err);
      setSaveMessage({ 
        text: "Failed to save settings: " + (err.response?.data?.error || err.message), 
        type: "error" 
      });
      toast.error("Failed to save settings: " + (err.message || "Unknown error"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewUserChange = (field, value) => {
    setNewUser(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${PRIMARY_API_URL}/api/auth/users`, {
        ...newUser,
        role: 'admin' // Force admin role
      });
      
      if (response.data.success) {
        toast.success('Admin account created successfully');
        
        // Add the new user to the state
        const newUserData = response.data.admin;
        newUserData.role = 'admin';
        
        setUsers(prevUsers => [
          ...prevUsers,
          newUserData
        ]);
        
        // Reset form and close modal
        setNewUser({
          name: '',
          email: '',
          phone: '',
          password: ''
        });
        
        setShowCreateModal(false);
      } else {
        toast.error(response.data.error || "Failed to create user");
      }
    } catch (error) {
      console.error("Error creating user:", error);
      toast.error(error.response?.data?.error || 
        (error.response?.status === 403 ? "You don't have permission to create users" : 
        "Failed to create user"));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleEditUser = async () => {
    // Validate required fields
    if (!editUser.name || !editUser.email) {
      toast.error("Name and email are required");
      return;
    }

    setIsLoading(true);
    
    try {
      // Get authentication token
      const token = localStorage.getItem('admin_token');
      if (!token) {
        toast.error("Authentication required. Please log in again.");
        setIsLoading(false);
        return;
      }
      
      // Set up headers with auth token
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      // Determine API endpoint based on user type
      const endpoint = editUser.role === 'admin'
        ? `/api/users/admin/${editUser.id}`
        : `/api/users/${editUser.id}`;
      
      // Prepare request body
      const requestBody = {
        name: editUser.name,
        email: editUser.email,
        password: editUser.password || undefined
      };
      
      // Add driver-specific fields if editing a driver
      if (editUser.role === 'admin') {
        requestBody.phone = editUser.phone || '';
        requestBody.status = editUser.status || 'active';
      }
      
      console.log(`Updating ${editUser.role} account:`, requestBody);
      
      // Make API request
      const response = await axios.put(endpoint, requestBody, { headers });
      
      if (response.data.success) {
        toast.success(`${editUser.role === 'admin' ? 'Admin' : 'Driver'} account updated successfully`);
        
        // Update users in state
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user.id === editUser.id
              ? { ...user, ...requestBody, password: undefined }
              : user
          )
        );
        
        // Reset form and close modal
        setEditUser({
          id: '',
          name: '',
          email: '',
          phone: '',
          password: '',
          status: 'active',
          role: ''
        });
        
        setShowEditModal(false);
      } else {
        toast.error(response.data.error || "Failed to update user");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error(error.response?.data?.error || 
        (error.response?.status === 403 ? "You don't have permission to update this user" : 
        "Failed to update user"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser || !selectedUser.id) {
      toast.error("No user selected for deletion");
      return;
    }

    setIsLoading(true);
    
    try {
      console.log(`Deleting user:`, selectedUser);
      
      // Get authentication token
      const token = localStorage.getItem('admin_token');
      if (!token) {
        toast.error("Authentication required. Please log in again.");
        setIsLoading(false);
        return;
      }
      
      // Set up headers with auth token
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      // API endpoint
      const endpoint = selectedUser.role === 'admin'
        ? `/api/users/admin/${selectedUser.id}`
        : `/api/users/${selectedUser.id}`;
      
      // Make DELETE request
      const response = await axios.delete(endpoint, { headers });
      
      if (response.data.success) {
        toast.success(`Admin account deleted successfully`);
        
        // Remove the deleted user from state
        setUsers(prevUsers => prevUsers.filter(user => user.id !== selectedUser.id));
        
        // Reset selection and close modal
        setSelectedUser(null);
        setShowDeleteModal(false);
      } else {
        toast.error(response.data.error || "Failed to delete user");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.response?.data?.error || 
        (error.response?.status === 403 ? "You don't have permission to delete this user" : 
        "Failed to delete user"));
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (user) => {
    console.log("Opening edit modal for user:", user);
    
    setSelectedUser(user);
    setEditUser({
      id: user.id,
      name: user.name,
      email: user.email,
      password: "", // Don't set the password
      role: user.role,
      phone: user.phone || "",
      status: user.status || "active"
    });
    
    console.log("Edit user data set:", {
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      phone: user.phone || "",
      status: user.status || "active"
    });
    
    setShowEditModal(true);
    
    // When using the inline form, we don't need to show a modal
    // Just scroll to the form
    window.scrollTo({
      top: 0, 
      behavior: 'smooth'
    });
  };

  const openDeleteModal = (user) => {
    console.log("Opening delete modal for user:", user);
    setSelectedUser(user);
    setShowDeleteModal(true);
  };
  
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-gray-400">Configure your business preferences and application settings</p>
        </div>
        <div className="flex space-x-2 items-center">
          {/* Database connection indicator */}
          <div className={`flex items-center px-3 py-1 rounded-md ${dbConnected ? 'bg-green-900/30 text-green-300' : 'bg-red-900/30 text-red-300'}`}>
            {dbConnected ? (
              <>
                <FiDatabase className="mr-2" />
                <span>Database Connected</span>
              </>
            ) : (
              <>
                <FiWifiOff className="mr-2" />
                <span>Offline Mode</span>
              </>
            )}
          </div>
          <RefreshButton 
            onClick={() => {
              if (activeSetting === "users") {
                // loadUsers function not implemented yet
                console.log("User management refresh - coming soon");
              } else {
                checkDatabaseConnection();
                fetchSettings();
              }
            }} 
            isLoading={isLoading || isLoadingUsers}
          />
        </div>
      </div>
      
      {/* Settings Tabs */}
      <div className="bg-[#121212] rounded-xl shadow-lg p-4 border border-white/20 mb-6">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveSetting("pricing")}
            className={`px-4 py-2 rounded-lg ${
              activeSetting === "pricing" 
                ? "bg-white text-black" 
                : "bg-black text-white hover:bg-black/50"
            }`}
          >
            Pricing
          </button>
          <button
            onClick={() => setActiveSetting("defaultLocation")}
            className={`px-4 py-2 rounded-lg ${
              activeSetting === "defaultLocation" 
                ? "bg-white text-black" 
                : "bg-black text-white hover:bg-black/50"
            }`}
          >
            Default Location
          </button>
          <button
            onClick={() => setActiveSetting("company")}
            className={`px-4 py-2 rounded-lg ${
              activeSetting === "company" 
                ? "bg-white text-black" 
                : "bg-black text-white hover:bg-black/50"
            }`}
          >
            Company Info
          </button>
        </div>
      </div>
      
      {/* Settings Forms */}
      <div className="bg-[#121212] rounded-xl shadow-lg p-6 border border-white/20 mb-6">
        {saveMessage.text && (
          <div className={`mb-6 p-4 ${
            saveMessage.type === "success" 
              ? "bg-green-900/30 text-green-200" 
              : saveMessage.type === "warning" 
                ? "bg-yellow-900/30 text-yellow-200"
                : "bg-red-900/30 text-red-200"
          } rounded-xl text-center`}>
            {saveMessage.text}
          </div>
        )}
        
        {activeSetting === "pricing" && (
          <div>
            <h2 className="text-xl font-bold text-white mb-4">Pricing Configuration</h2>
            <p className="text-gray-400 mb-6">Set your base pricing parameters for booking calculations</p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-white">
                  Base Fee (€)
                </label>
                <input 
                  type="number"
                  value={settings.pricing.baseFee}
                  onChange={(e) => handleChange("pricing", "baseFee", parseFloat(e.target.value))}
                  className="w-full p-4 bg-black border-0 rounded-xl focus:ring-1 focus:ring-white/30 ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                />
                <p className="mt-1 text-xs text-gray-400">Starting fee for all luxury journeys</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-white">
                  Price per Kilometer (€)
                </label>
                <input 
                  type="number"
                  value={settings.pricing.pricePerKm}
                  onChange={(e) => handleChange("pricing", "pricePerKm", parseFloat(e.target.value))}
                  className="w-full p-4 bg-black border-0 rounded-xl focus:ring-1 focus:ring-white/30 ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                />
                <p className="mt-1 text-xs text-gray-400">Amount charged per kilometer traveled</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-white">
                  Price per Luggage Item (€)
                </label>
                <input 
                  type="number"
                  value={settings.pricing.pricePerLuggage}
                  onChange={(e) => handleChange("pricing", "pricePerLuggage", parseFloat(e.target.value))}
                  className="w-full p-4 bg-black border-0 rounded-xl focus:ring-1 focus:ring-white/30 ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                />
                <p className="mt-1 text-xs text-gray-400">Additional charge per piece of luggage</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-white">
                  Passenger Surcharge (%)
                </label>
                <input 
                  type="number"
                  value={Math.round(settings.pricing.surchargePerPassenger * 100)}
                  onChange={(e) => handleChange("pricing", "surchargePerPassenger", parseFloat(e.target.value))}
                  min="0"
                  max="100"
                  step="1"
                  className="w-full p-4 bg-black border-0 rounded-xl focus:ring-1 focus:ring-white/30 ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                />
                <p className="mt-1 text-xs text-gray-400">Percentage increase for each additional passenger beyond the first</p>
              </div>
            </div>
          </div>
        )}
        
        {activeSetting === "defaultLocation" && (
          <div>
            <h2 className="text-xl font-bold text-white mb-4">Default Location</h2>
            <p className="text-gray-400 mb-6">Set your default pickup location for the booking system</p>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-3">
                <label className="block text-sm font-medium mb-2 text-white">
                  Location Name
                </label>
                <input 
                  type="text"
                  value={settings.defaultLocation.name}
                  onChange={(e) => handleChange("defaultLocation", "name", e.target.value)}
                  className="w-full p-4 bg-black border-0 rounded-xl focus:ring-1 focus:ring-white/30 ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                />
                <p className="mt-1 text-xs text-gray-400">Display name for your default location</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-white">
                  Latitude
                </label>
                <input 
                  type="number"
                  step="0.000001"
                  value={settings.defaultLocation.lat}
                  onChange={(e) => handleChange("defaultLocation", "lat", parseFloat(e.target.value))}
                  className="w-full p-4 bg-black border-0 rounded-xl focus:ring-1 focus:ring-white/30 ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-white">
                  Longitude
                </label>
                <input 
                  type="number"
                  step="0.000001"
                  value={settings.defaultLocation.lng}
                  onChange={(e) => handleChange("defaultLocation", "lng", parseFloat(e.target.value))}
                  className="w-full p-4 bg-black border-0 rounded-xl focus:ring-1 focus:ring-white/30 ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                />
              </div>
              
              <div className="md:col-span-3 p-4 bg-black/50 rounded-xl border border-white/30">
                <p className="text-gray-400 text-sm">
                  These coordinates are used as default map center for pickup/dropoff selection and when initializing new booking forms.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {activeSetting === "company" && (
          <div>
            <h2 className="text-xl font-bold text-white mb-4">Company Information</h2>
            <p className="text-gray-400 mb-6">Update your business details shown on invoices and emails</p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2 text-white">
                  Company Name
                </label>
                <input 
                  type="text"
                  value={settings.company.name}
                  onChange={(e) => handleChange("company", "name", e.target.value)}
                  className="w-full p-4 bg-black border-0 rounded-xl focus:ring-1 focus:ring-white/30 ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-white">
                  Email Address
                </label>
                <input 
                  type="email"
                  value={settings.company.email}
                  onChange={(e) => handleChange("company", "email", e.target.value)}
                  className="w-full p-4 bg-black border-0 rounded-xl focus:ring-1 focus:ring-white/30 ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-white">
                  Phone Number
                </label>
                <input 
                  type="text"
                  value={settings.company.phone}
                  onChange={(e) => handleChange("company", "phone", e.target.value)}
                  className="w-full p-4 bg-black border-0 rounded-xl focus:ring-1 focus:ring-white/30 ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2 text-white">
                  Website
                </label>
                <input 
                  type="text"
                  value={settings.company.website}
                  onChange={(e) => handleChange("company", "website", e.target.value)}
                  className="w-full p-4 bg-black border-0 rounded-xl focus:ring-1 focus:ring-white/30 ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Save Button - Only show for pricing, location and company settings */}
      {(activeSetting === "pricing" || activeSetting === "defaultLocation" || activeSetting === "company") && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="px-6 py-3 bg-gradient-to-r from-white to-yellow-600 text-black rounded-lg hover:from-white hover:to-yellow-500 transition-colors shadow-lg font-medium flex items-center"
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                Saving...
              </>
            ) : (
              <>Save Settings</>
            )}
          </button>
        </div>
      )}
      
      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-bold text-white mb-4">Create New User</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">
                    User Type
                  </label>
                  <select
                    value={newUser.role}
                    onChange={(e) => handleNewUserChange("role", e.target.value)}
                    className="w-full p-3 bg-black border-0 rounded-lg focus:ring-1 focus:ring-white/30 ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                  >
                    <option value="admin">Admin</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">
                    Name
                  </label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(e) => handleNewUserChange("name", e.target.value)}
                    className="w-full p-3 bg-black border-0 rounded-lg focus:ring-1 focus:ring-white/30 ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                    placeholder="Full name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => handleNewUserChange("email", e.target.value)}
                    className="w-full p-3 bg-black border-0 rounded-lg focus:ring-1 focus:ring-white/30 ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                    placeholder="Email address"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">
                    Password
                  </label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => handleNewUserChange("password", e.target.value)}
                    className="w-full p-3 bg-black border-0 rounded-lg focus:ring-1 focus:ring-white/30 ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                    placeholder="Password"
                  />
                </div>
                
                {newUser.role === "admin" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-300">
                        Phone Number
                      </label>
                      <input
                        type="text"
                        value={newUser.phone}
                        onChange={(e) => handleNewUserChange("phone", e.target.value)}
                        className="w-full p-3 bg-black border-0 rounded-lg focus:ring-1 focus:ring-white/30 ring-offset-0 transition-colors duration-300 outline-none focus:outline-none"
                        placeholder="Phone number"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
            
            <div className="bg-gray-900 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                disabled={isLoading}
                className="px-4 py-2 bg-white text-black rounded-lg hover:bg-white flex items-center"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>Create User</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete User Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Delete Admin Account</h3>
              <p className="text-gray-300 mb-4">
                Are you sure you want to delete this admin account? This action cannot be undone.
              </p>
            </div>
            
            <div className="bg-gray-900 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 flex items-center"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  <>Delete Account</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings; 